import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";

import infoIcon from "../../../assets/images/info-icon.png";
import ArrowUpwardIcon from "../../../assets/images/ArrowUpwardIcon_green.svg";
import ArrowDownwardIcon from "../../../assets/images/ArrowDownwardIcon_red.svg";
import { useSalesContext } from "../../SalesContext";

interface MissedCallData {
  partyId: number;
  partyName?: string;
  speciality?: string;
  frequency?: number;
  visits?: number;
  dm?: number;
  lastReportedDM?: number;
}

interface MonthlyData {
  partyId: number;
  partyName?: string;
  speciality?: string;
  frequency?: number;
  dcrDatesByMonth?: { [key: string]: number[] };
  visitsByMonth?: { [key: string]: number };
  potentialByMonth?: { [key: string]: number };
  dm?: number;
  visits?: number;
  lastReportedDM?: number;
}

type KpiData = MissedCallData | MonthlyData;

const PartyTable: React.FC = () => {
  const { kpiData, loading } = useSalesContext();
  const {
    Tabledata = [],
    categoryName = "",
    categoryCurrent = 0,
    categoryTotal = 0,
    isLimited = false,
    isMissedDoctor = false,
    isMissedCall = false,
    missedDoctor = "",
    missedCalls = "",
  } = kpiData || {};

  const data: KpiData[] = Array.isArray(Tabledata) ? Tabledata : [];

  const processedData =
    isMissedCall || isLimited
      ? data.map((row) => ({
          ...row,
          month: "",
          dcrDates: [],
          visitsForMonth: row.visits ?? 0,
          potential: 0,
        }))
      : data
          .filter(
            (row): row is MonthlyData =>
              !!row.dcrDatesByMonth && typeof row.dcrDatesByMonth === "object"
          )
          .flatMap((row) =>
            Object.keys(row.dcrDatesByMonth || {}).map((month) => ({
              ...row,
              month,
              dcrDates: row.dcrDatesByMonth?.[month] || [],
              visitsForMonth: row.visitsByMonth?.[month] ?? 0,
              potential: isMissedDoctor
                ? row.potentialByMonth?.[month] ?? 0
                : 0,
            }))
          );

  if (loading || !kpiData) {
    return (
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight: 400,
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          padding: "0 10px 0 0 ",
          border: "1px solid #007bff",
          display: "flex",
          alignItems: "center",
        }}
      >
        {isMissedDoctor || isMissedCall || isLimited ? (
          <Box
            sx={{
              backgroundColor: "#cbf5ff",
              display: "flex",
              alignItems: "center",
              padding: "7px 15px 7px 5px",
            }}
          >
            <img
              src={infoIcon}
              alt="Info Icon"
              style={{ width: 24, height: 24, color: "#007bff" }}
            />
            <span
              style={{
                marginLeft: 5,
                marginRight: 5,
                fontWeight: "bold",
              }}
            >
              {isMissedDoctor ? "Missed Doctors" : "Missed Calls"}
            </span>
          </Box>
        ) : (
          <span style={{ marginLeft: 10 }}>{`${categoryName}-`}</span>
        )}
        <span
          style={{
            marginLeft: 10,
            color: "#800080",
            fontWeight: "bold",
          }}
        >
          {isMissedDoctor || isMissedCall
            ? missedDoctor || missedCalls
            : `${categoryCurrent}/${categoryTotal}`}
        </span>
        {!isMissedDoctor && (
          <Box
            sx={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              padding: "7px 15px 7px 5px",
            }}
          >
            <span style={{ marginLeft: 5, marginRight: 5 }}>
              DMS and Last Reported DMS (0)
            </span>
          </Box>
        )}
      </Box>

      <Table
        sx={{
          minWidth: 650,
          "& .MuiTableHead-root": {
            position: "sticky",
            top: 0,
            zIndex: 1,
            backgroundColor: "#fff",
          },
          "& .MuiTableCell-root": {
            padding: "7px",
            fontSize: "12px",
          },
          "& .MuiTableCell-head": {
            fontWeight: "bold",
            borderBottom: "2px solid #000",
          },
        }}
        aria-label="party table"
      >
        <TableHead>
          <TableRow>
            <TableCell>Party ID</TableCell>
            <TableCell>Party Name</TableCell>
            {!isMissedCall && !isLimited && <TableCell>Month</TableCell>}
            <TableCell>Speciality</TableCell>
            <TableCell>Frequency</TableCell>
            {!isMissedCall && !isLimited && <TableCell>DCR Dates</TableCell>}
            {isMissedDoctor && <TableCell>Potential</TableCell>}
            <TableCell>DM</TableCell>
            <TableCell>Visits</TableCell>
            <TableCell>Last Reported DMS</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {processedData.length > 0 ? (
            processedData.map((row, index) => {
              const lastReportedDMValue = row.lastReportedDM ?? 0;
              const isValidNumber = !isNaN(lastReportedDMValue);
              const isPositive = isValidNumber && lastReportedDMValue > 50;
              const isNegative = isValidNumber && lastReportedDMValue <= 50;

              return (
                <TableRow
                  key={`${row.partyId}-${row.month || index}-${index}`}
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#f5f5f5" : "#e6e6e6",
                  }}
                >
                  <TableCell>{row.partyId}</TableCell>
                  <TableCell>{row.partyName || "N/A"}</TableCell>
                  {!isMissedCall && !isLimited && (
                    <TableCell>{row.month}</TableCell>
                  )}
                  <TableCell>{row.speciality || "N/A"}</TableCell>
                  <TableCell>{row.frequency ?? 0}</TableCell>
                  {!isMissedCall && !isLimited && (
                    <TableCell>{`(${row.dcrDates.join(",")})`}</TableCell>
                  )}
                  {isMissedDoctor && <TableCell>{row.potential}</TableCell>}
                  <TableCell>{row.dm ?? 0}</TableCell>
                  <TableCell>{row.visitsForMonth}</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {isPositive ? (
                        <img
                          src={ArrowUpwardIcon}
                          alt="Up Arrow"
                          style={{ width: 16, height: 16, marginRight: 4 }}
                        />
                      ) : isNegative ? (
                        <img
                          src={ArrowDownwardIcon}
                          alt="Down Arrow"
                          style={{ width: 16, height: 16, marginRight: 4 }}
                        />
                      ) : null}
                      <Typography
                        sx={{
                          color: isNegative
                            ? "red"
                            : isPositive
                            ? "green"
                            : "black",
                          fontSize: "12px",
                        }}
                      >
                        {lastReportedDMValue}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell
                colSpan={
                  isMissedDoctor ? 9 : isMissedCall ? 7 : isLimited ? 7 : 9
                }
                align="center"
              >
                No data available
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PartyTable;
